import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Play, Pause, Mic, ArrowLeft, Link2, Facebook, Twitter, Check, Download } from "lucide-react";
import { toast } from "sonner";
import { api, resolveMedia, formatApiError } from "../lib/api";
import { useSEO } from "../lib/useSEO";
import { usePlayer } from "../context/AudioContext";
import { Reveal } from "../components/Reveal";
import { SectionLabel } from "../components/SectionLabel";
import { VaultCard } from "../components/VaultCard";

export default function VaultDetail() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [copied, setCopied] = useState(false);
  const [buying, setBuying] = useState(false);
  const { playTrack, track, playing } = usePlayer();

  const buy = async () => {
    setBuying(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        slug: entry.slug,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiError(e));
      setBuying(false);
    }
  };

  useEffect(() => {
    setData(null);
    setError(false);
    api.get(`/vault/${slug}`).then((r) => setData(r.data)).catch(() => setError(true));
  }, [slug]);

  const entry = data?.entry;
  useSEO(entry?.title, entry?.description, entry?.artwork_url);

  if (error) {
    return (
      <div className="pt-48 pb-32 text-center" data-testid="vault-not-found">
        <p className="font-serif text-4xl text-cream">This piece isn't in the Vault.</p>
        <Link to="/vault" className="mt-6 inline-block font-mono text-[11px] tracking-[0.3em] uppercase text-gold hover:text-goldglow">
          ← Back to the Vault
        </Link>
      </div>
    );
  }
  if (!entry) {
    return <div className="pt-48 pb-32 text-center text-sand font-mono text-xs tracking-[0.3em] uppercase">Opening the Vault…</div>;
  }

  const isPlaying = track?.slug === entry.slug && playing;
  const shareUrl = window.location.href;
  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {}
  };

  return (
    <div data-testid="vault-song-detail" className="pt-28 sm:pt-36 pb-20">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
        <Link to="/vault" data-testid="vault-back-link" className="inline-flex items-center gap-2 font-mono text-[10px] tracking-[0.3em] uppercase text-sand hover:text-gold transition-colors">
          <ArrowLeft size={13} /> The Royal Vault
        </Link>

        <div className="mt-10 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-5">
            <motion.div
              initial={{ opacity: 0, scale: 0.97 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
              className="relative overflow-hidden border border-gold/25 lg:sticky lg:top-28"
            >
              {entry.artwork_url ? (
                <img src={resolveMedia(entry.artwork_url)} alt={entry.title} data-testid="vault-detail-artwork" className="w-full aspect-square object-cover" />
              ) : (
                <div className="w-full aspect-square bg-velvet flex items-center justify-center font-serif italic text-gold/40 text-3xl">Queen Denae</div>
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-obsidian/70 to-transparent" />
              <span className="absolute top-5 left-5 font-mono text-[10px] tracking-[0.3em] uppercase text-goldglow bg-obsidian/70 backdrop-blur px-3 py-1.5">
                From the Royal Vault
              </span>
              <button
                onClick={() => playTrack({ ...entry, tag: "The Royal Vault" })}
                data-testid="vault-detail-play-btn"
                aria-label={`Play ${entry.title}`}
                className="absolute bottom-6 left-6 h-16 w-16 rounded-full bg-gold text-obsidian flex items-center justify-center hover:bg-goldglow transition-colors shadow-xl shadow-black/50"
              >
                {isPlaying ? <Pause size={24} /> : <Play size={24} className="ml-1" />}
              </button>
            </motion.div>
          </div>

          <div className="lg:col-span-7">
            <Reveal>
              <div className="flex flex-wrap items-center gap-4 font-mono text-[10px] tracking-[0.3em] uppercase text-gold/80">
                <span data-testid="vault-detail-category">{entry.category}</span>
                {entry.release_date && <span className="text-sand/60">{entry.release_date}</span>}
                <span className="text-sand/60">{(entry.play_count || 0).toLocaleString()} plays</span>
              </div>
              <h1 className="mt-4 font-serif text-5xl sm:text-7xl tracking-tight text-cream leading-[0.95]" data-testid="vault-detail-title">
                {entry.title}
              </h1>
              {entry.description && (
                <p className="mt-6 font-serif italic text-xl sm:text-2xl text-goldglow/90 leading-snug max-w-xl" data-testid="vault-detail-description">
                  {entry.description}
                </p>
              )}
              <div className="mt-8 flex flex-wrap gap-4">
                <button
                  onClick={() => playTrack({ ...entry, tag: "The Royal Vault" })}
                  data-testid="vault-detail-listen-btn"
                  className="h-13 px-8 py-4 inline-flex items-center gap-3 bg-gold text-obsidian font-mono text-[11px] tracking-[0.3em] uppercase hover:bg-goldglow transition-colors"
                >
                  {isPlaying ? <Pause size={15} /> : <Play size={15} />} {isPlaying ? "Playing Now" : "Listen Now"}
                </button>
                {entry.voice_note_url && (
                  <button
                    onClick={() =>
                      playTrack({
                        title: `${entry.title} — Original Voice Note`,
                        audio_url: entry.voice_note_url,
                        artwork_url: entry.artwork_url,
                        tag: "Voice Note",
                      })
                    }
                    data-testid="vault-detail-voicenote-btn"
                    className="h-13 px-8 py-4 inline-flex items-center gap-3 border border-gold/40 text-gold font-mono text-[11px] tracking-[0.3em] uppercase hover:bg-gold hover:text-obsidian transition-colors"
                  >
                    <Mic size={15} /> Hear the Voice Note
                  </button>
                )}
                {entry.for_sale && Number(entry.price) > 0 && (
                  <button
                    onClick={buy}
                    disabled={buying}
                    data-testid="buy-single-btn"
                    className="h-13 px-8 py-4 inline-flex items-center gap-3 border border-goldglow/60 bg-goldglow/10 text-goldglow font-mono text-[11px] tracking-[0.3em] uppercase hover:bg-goldglow hover:text-obsidian transition-colors disabled:opacity-60"
                  >
                    <Download size={15} /> {buying ? "One Moment…" : `Own This Song — $${Number(entry.price).toFixed(2)}`}
                  </button>
                )}
              </div>
            </Reveal>

            {entry.video_url && (
              <Reveal className="mt-12">
                <SectionLabel>Watch</SectionLabel>
                <div className="aspect-video border border-gold/20 bg-gallery">
                  <iframe src={entry.video_url} title={entry.title} className="w-full h-full" allowFullScreen data-testid="vault-detail-video" />
                </div>
              </Reveal>
            )}

            {entry.story && (
              <Reveal className="mt-12">
                <SectionLabel>The Story</SectionLabel>
                <p className="text-sand leading-relaxed whitespace-pre-line max-w-2xl" data-testid="vault-detail-story">{entry.story}</p>
              </Reveal>
            )}

            {entry.lyrics && (
              <Reveal className="mt-12">
                <SectionLabel>Lyrics</SectionLabel>
                <div className="border-l-2 border-gold/30 pl-6" data-testid="vault-detail-lyrics">
                  <p className="font-serif italic text-lg text-cream/80 leading-loose whitespace-pre-line">{entry.lyrics}</p>
                </div>
              </Reveal>
            )}

            <Reveal className="mt-12">
              <SectionLabel>Share This Piece</SectionLabel>
              <div className="flex flex-wrap gap-3">
                <button
                  onClick={copyLink}
                  data-testid="share-copy-link-btn"
                  className="h-11 px-5 inline-flex items-center gap-2 border border-gold/30 text-sand font-mono text-[10px] tracking-[0.25em] uppercase hover:border-gold hover:text-gold transition-colors"
                >
                  {copied ? <Check size={13} /> : <Link2 size={13} />} {copied ? "Copied" : "Copy Link"}
                </button>
                <a
                  href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`}
                  target="_blank" rel="noopener noreferrer"
                  data-testid="share-facebook-btn"
                  className="h-11 px-5 inline-flex items-center gap-2 border border-gold/30 text-sand font-mono text-[10px] tracking-[0.25em] uppercase hover:border-gold hover:text-gold transition-colors"
                >
                  <Facebook size={13} /> Facebook
                </a>
                <a
                  href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(`${entry.title} — from the Royal Vault, Queen Denae's private archive`)}`}
                  target="_blank" rel="noopener noreferrer"
                  data-testid="share-x-btn"
                  className="h-11 px-5 inline-flex items-center gap-2 border border-gold/30 text-sand font-mono text-[10px] tracking-[0.25em] uppercase hover:border-gold hover:text-gold transition-colors"
                >
                  <Twitter size={13} /> Share
                </a>
              </div>
            </Reveal>
          </div>
        </div>

        {data?.related?.length > 0 && (
          <div className="mt-24">
            <SectionLabel>Also In The Vault</SectionLabel>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
              {data.related.map((r, i) => (
                <VaultCard key={r.id} entry={r} index={i} />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
